import {
  isDefined,
  isNotDefined,
  Logger,
  type TeamUpdateData,
  toResponseError,
  type TeamData,
  type TeamId,
  type TeamUpdateInput,
  type TeamUpdateSuccess,
} from '@terros/common'
import { useTantalimMutation } from '../mutation'
import { mutationUpdate, type UpdateHookResponse } from '../hooks'
import { useTeamCache } from './cache'

const logger = new Logger('useTeams')

export function useUpdateTeam(): UpdateHookResponse<TeamUpdateData, TeamData> {
  logger.verbose('calling useUpdateTeam')
  const cache = useTeamCache()

  const mutation = useTantalimMutation<TeamUpdateInput, TeamUpdateSuccess>({ url: '/team/update' })

  function getCachedTeam(teamId: TeamId): TeamData | undefined {
    return cache.getTeamData(teamId)?.team ?? cache.getItem(teamId)
  }

  async function update(team: TeamUpdateData): Promise<TeamData> {
    try {
      logger.verbose('starting update')
      const previousTeam = getCachedTeam(team.teamId)
      const previousParentId = previousTeam?.parentId
      const response = await mutation.mutateAsync({ team })
      cache.partialUpdateTeam(team)
      cache.partialUpdateTeam(response.team)
      if (isDefined(team.parentId)) cache.clearItem(team.parentId)
      if (isParentChange(team, previousParentId)) cache.clearItem(previousParentId)
      if (isHierarchyChange(team, previousTeam)) cache.clearList()
      // Archiving or restoring moves the team between the active and archived lists,
      // which patching the cached rows in place cannot express
      if (isDefined(team.isDeleted)) cache.clearList()
      logger.verbose('done with update')
      return response.team
    } catch (error) {
      throw toResponseError(error)
    }
  }

  return mutationUpdate(mutation, update)
}

function isParentChange(team: TeamUpdateData, previousParentId: TeamId | undefined): previousParentId is TeamId {
  return isDefined(previousParentId) && 'parentId' in team && team.parentId !== previousParentId
}

function isHierarchyChange(team: TeamUpdateData, previousTeam: TeamData | undefined): boolean {
  if (isNotDefined(previousTeam)) return 'level' in team || 'parentId' in team
  return isLevelChange(team, previousTeam) || isParentIdChange(team, previousTeam)
}

function isLevelChange(team: TeamUpdateData, previousTeam: TeamData): boolean {
  return 'level' in team && team.level !== previousTeam.level
}

function isParentIdChange(team: TeamUpdateData, previousTeam: TeamData): boolean {
  return 'parentId' in team && (team.parentId ?? undefined) !== previousTeam.parentId
}
